import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiCheck, FiX, FiTrash2, FiSearch } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDialog, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { today, unwrapList } from '../../utils/format';
import SearchableSelect from '../../components/common/SearchableSelect';

const emptyForm = { employee_id: '', leave_type_id: '', from_date: today(), to_date: today(), reason: '' };

export default function Leaves() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', status: '', per_page: 100 });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.leaves.list(params);
      const list = Array.isArray(data.data) ? data.data : data.leaves || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load leaves', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.leaves.types().then(({ data }) => {
      setLeaveTypes(unwrapList(data, 'leave_types'));
    }).catch(() => {});
    api.employees.list({ per_page: 500 }).then(({ data }) => {
      setEmployees(unwrapList(data, 'employees'));
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    const errs = {};
    if (!form.employee_id) errs.employee_id = 'Employee is required';
    if (!form.leave_type_id) errs.leave_type_id = 'Leave type is required';
    if (!form.from_date) errs.from_date = 'From date is required';
    if (!form.to_date) errs.to_date = 'To date is required';
    if (!form.reason) errs.reason = 'Reason is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      await api.leaves.create({ ...form, employee_id: Number(form.employee_id), leave_type_id: Number(form.leave_type_id) });
      toast('success', '', 'Leave request created');
      setModal(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      showError('Failed to create leave', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const review = async (row, approve) => {
    const res = await confirmDialog({
      title: approve ? 'Approve leave?' : 'Reject leave?',
      confirmText: approve ? 'Yes, approve' : 'Yes, reject',
      icon: approve ? 'question' : 'warning',
    });
    if (!res.isConfirmed) return;
    try {
      if (approve) await api.leaves.approve(row.id);
      else await api.leaves.reject(row.id);
      toast('success', '', `Leave ${approve ? 'approved' : 'rejected'}`);
      load();
    } catch (err) {
      showError('Action failed', extractError(err));
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete('this leave request');
    if (!res.isConfirmed) return;
    try {
      await api.leaves.remove(row.id);
      toast('success', '', 'Leave deleted');
      load();
    } catch (err) {
      showError('Failed to delete leave', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Leaves"
        subtitle="Employee leave requests and approvals."
        crumbs={[{ label: 'HR' }, { label: 'Leaves' }]}
        actions={hasPermission('create-leaves') && (
          <button className="btn btn-primary" onClick={() => { setErrors({}); setModal(true); }}><FiPlus /> Add Leave</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search employee/reason..." value={params.search} onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })} />
            </div>
            <SearchableSelect options={[{ value: '', label: 'All Status' }, { value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }]} value={params.status} onChange={(v) => setParams({ ...params, status: v, page: 1 })} placeholder="All Status" />
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="leaves" label="leaves" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No leave requests found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'employee', label: 'Employee', render: (r) => (r.employee ? r.employee.name : '-') },
              { key: 'leave_type', label: 'Type', render: (r) => (r.leave_type ? r.leave_type.name : '-') },
              { key: 'from_date', label: 'From' },
              { key: 'to_date', label: 'To' },
              { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              { key: 'reason', label: 'Reason' },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {r.status === 'pending' && hasPermission('approve-leaves') && (
                    <>
                      <button className="btn-icon success" title="Approve" onClick={() => review(r, true)}><FiCheck /></button>
                      <button className="btn-icon danger" title="Reject" onClick={() => review(r, false)}><FiX /></button>
                    </>
                  )}
                  {hasPermission('delete-leaves') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>

      <Modal open={modal} onClose={() => { setErrors({}); setModal(false); }} title="Add Leave Request"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
        </>}
      >
        <div className="grid-2">
          <div className="form-group"><label className="form-label">Employee <span className="req">*</span></label>
            <SearchableSelect options={employees} value={form.employee_id} onChange={(v) => { setForm({ ...form, employee_id: v }); if (errors.employee_id) setErrors((p) => ({ ...p, employee_id: '' })); }} isInvalid={!!errors.employee_id} placeholder="-- Select employee --" />
            {errors.employee_id && <div className="invalid-feedback">{errors.employee_id}</div>}</div>
          <div className="form-group"><label className="form-label">Leave Type <span className="req">*</span></label>
            <SearchableSelect options={leaveTypes} value={form.leave_type_id} onChange={(v) => { setForm({ ...form, leave_type_id: v }); if (errors.leave_type_id) setErrors((p) => ({ ...p, leave_type_id: '' })); }} getLabel={(t) => `${t.name} (${t.allowed_days} days)`} isInvalid={!!errors.leave_type_id} placeholder="-- Select type --" />
            {errors.leave_type_id && <div className="invalid-feedback">{errors.leave_type_id}</div>}</div>
          <div className="form-group"><label className="form-label">From Date <span className="req">*</span></label>
            <input className={`form-control ${errors.from_date ? 'is-invalid' : ''}`} type="date" value={form.from_date} onChange={(e) => { setForm({ ...form, from_date: e.target.value }); if (errors.from_date) setErrors((p) => ({ ...p, from_date: '' })); }} />
            {errors.from_date && <div className="invalid-feedback">{errors.from_date}</div>}</div>
          <div className="form-group"><label className="form-label">To Date <span className="req">*</span></label>
            <input className={`form-control ${errors.to_date ? 'is-invalid' : ''}`} type="date" value={form.to_date} onChange={(e) => { setForm({ ...form, to_date: e.target.value }); if (errors.to_date) setErrors((p) => ({ ...p, to_date: '' })); }} />
            {errors.to_date && <div className="invalid-feedback">{errors.to_date}</div>}</div>
        </div>
        <div className="form-group"><label className="form-label">Reason <span className="req">*</span></label>
          <textarea className={`form-control ${errors.reason ? 'is-invalid' : ''}`} value={form.reason} onChange={(e) => { setForm({ ...form, reason: e.target.value }); if (errors.reason) setErrors((p) => ({ ...p, reason: '' })); }} />
          {errors.reason && <div className="invalid-feedback">{errors.reason}</div>}</div>
      </Modal>
    </div>
  );
}